import { useState, useEffect, useCallback } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "./firebase";
import { seedIfEmpty } from "./useFirestore";
import Login from "./Login";
import CRM from "./CRM";
import Portal from "./Portal";

export default function App() {
  const [user, setUser] = useState(undefined);
  const [role, setRole] = useState("admin");
  const [userDoc, setUserDoc] = useState(null);
  const [seeded, setSeeded] = useState(false);
  const [waitingReg, setWaitingReg] = useState(null);

  useEffect(() => {
    const handler = (e) => setWaitingReg(e.detail.registration);
    window.addEventListener('swUpdateAvailable', handler);
    return () => window.removeEventListener('swUpdateAvailable', handler);
  }, []);

  const handleReload = useCallback(() => {
    if (waitingReg && waitingReg.waiting) {
      waitingReg.waiting.postMessage({ type: 'SKIP_WAITING' });
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
    } else {
      window.location.reload();
    }
  }, [waitingReg]);

  // Check if this is a portal URL — if so, skip auth entirely
  const isPortal = window.location.pathname.startsWith("/portal/")
    || new URLSearchParams(window.location.search).get("token");

  useEffect(() => {
    if (isPortal) return; // portal handles its own data loading
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u ?? null);
      if (u) {
        // Load user role doc — default to admin if no doc exists (owner/first user)
        try {
          const snap = await getDoc(doc(db, "users", u.uid));
          if (snap.exists()) {
            const data = snap.data();
            setUserDoc({ id: snap.id, ...data });
            setRole(data.active === false ? "suspended" : (data.role || "employee"));
          } else {
            // Fall back to email lookup (doc created before first login)
            const q = query(collection(db, "users"), where("email", "==", u.email));
            const emailSnap = await getDocs(q);
            if (!emailSnap.empty) {
              const data = emailSnap.docs[0].data();
              setUserDoc({ id: emailSnap.docs[0].id, ...data });
              setRole(data.active === false ? "suspended" : (data.role || "employee"));
            } else {
              setUserDoc(null);
              setRole("employee");
            }
          }
        } catch {
          setUserDoc(null);
          setRole("employee");
        }
        if (!seeded) { await seedIfEmpty(); setSeeded(true); }
      } else {
        setUserDoc(null);
        setRole("admin");
      }
    });
    return unsub;
  }, [seeded, isPortal]);

  const updateBanner = waitingReg ? (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
      background: "#1a0a0a", borderBottom: "2px solid #b91c1c",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 16px", minHeight: 48,
    }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: "#f5f5f4", letterSpacing: "0.01em" }}>
        Mise à jour disponible&nbsp;/&nbsp;Update available
      </span>
      <button
        onClick={handleReload}
        style={{
          minHeight: 44, minWidth: 44, padding: "0 18px",
          background: "#b91c1c", color: "#fff", border: "none",
          borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer",
          fontFamily: "inherit", letterSpacing: "0.02em",
        }}
      >
        Recharger&nbsp;/&nbsp;Reload
      </button>
    </div>
  ) : null;

  // Portal route — no auth required
  if (isPortal) return <>{updateBanner}<Portal /></>;

  if (user === undefined) return (
    <>
      {updateBanner}
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",fontFamily:"DM Sans,sans-serif",color:"#a8a29e",fontSize:14,background:"#1a0a0a"}}>
        Chargement...
      </div>
    </>
  );

  if (!user) return <>{updateBanner}<Login onLogin={setUser} /></>;

  return <>{updateBanner}<CRM onLogout={() => signOut(auth)} user={user} role={role} userDoc={userDoc} /></>;
}